import { useCallback, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import useDebounce from '../../../components/Shared/Delay/index.tsx';
import { listFiltersAtom } from '../../../components/Shared/SearchFilter/listFiltersAtom.js';
import { USER_STATUS_MESSAGES } from '../config/userStatusCopy';
import { userService } from '../services/userService';

const DEFAULT_FILTERS = {
  limit: 20,
  search: '',
  page: 1,
  sortOrder: 'desc',
  sortField: 'created_at',
  selectedOrganisation: null,
  organisation_id: '',
  selectedProfile: null,
  profile_id: '',
  selectedStatus: null,
  is_active: '',
};

export default function useUserList() {
  const { showSnackBar } = useSnackBar();
  const [allListFilters, setAllListFilters] = useRecoilState(listFiltersAtom);
  const filtersObj = allListFilters.userList || DEFAULT_FILTERS;

  const setFiltersObj = useCallback(
    (newFilters) => {
      setAllListFilters((prev) => ({ ...prev, userList: newFilters }));
    },
    [setAllListFilters]
  );

  const [userResponse, setUserResponse] = useState({ count: 0, list: [] });
  const [isLoading, setIsLoading] = useState(false);

  const getUserList = useCallback(
    async (filterObj) => {
      setIsLoading(true);
      try {
        const res = await userService.getUserList(filterObj);
        setUserResponse(res?.status === 200 ? res?.data || {} : {});
      } catch {
        showSnackBar('Error fetching roles:', 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [showSnackBar]
  );

  const debouncedSearch = useDebounce(filtersObj.search, 500);

  useEffect(() => {
    if (debouncedSearch || debouncedSearch === '') {
      getUserList({ ...filtersObj, search: debouncedSearch, page: 1 });
    }
  }, [debouncedSearch]);

  // const handleSearch = useCallback(
  //   (value) => {
  //     setFiltersObj({ ...filtersObj, search: value, page: 1 });
  //   },
  //   [filtersObj, setFiltersObj]
  // );

  const handleSearch = useCallback(
    (value) => {
      const searchValue = value
        .replace(/^\s+/, '')
        .replace(/\s{2,}/g, ' ');

      setFiltersObj({
        ...filtersObj,
        search: searchValue,
        page: 1,
      });
    },
    [filtersObj, setFiltersObj]
  );

  const handleOrganisationChange = useCallback(
    (org) => {
      const nextFilters = {
        ...filtersObj,
        selectedOrganisation: org || null,
        organisation_id: org?.organisation_id || '',
        selectedProfile: null,
        profile_id: '',
        selectedStatus: null,
        is_active: '',
        page: 1,
      };
      setFiltersObj(nextFilters);
      getUserList(nextFilters);
    },
    [filtersObj, getUserList, setFiltersObj]
  );

  const handleProfileChange = useCallback(
    (profile) => {
      const nextFilters = {
        ...filtersObj,
        selectedProfile: profile || null,
        profile_id: profile?.id ? String(profile.id) : '',
        page: 1,
      };
      setFiltersObj(nextFilters);
      getUserList(nextFilters);
    },
    [filtersObj, getUserList, setFiltersObj]
  );

  const handleStatusChange = useCallback(
    (statusOption) => {
      const nextFilters = {
        ...filtersObj,
        selectedStatus: statusOption || null,
        is_active: statusOption?.value ?? '',
        page: 1,
      };
      setFiltersObj(nextFilters);
      getUserList(nextFilters);
    },
    [filtersObj, getUserList, setFiltersObj]
  );

  const applyFilters = useCallback(
    (partialFilters) => {
      const nextFilters = {
        ...filtersObj,
        ...partialFilters,
        page: partialFilters.page ?? 1,
      };
      setFiltersObj(nextFilters);
      getUserList(nextFilters);
    },
    [filtersObj, getUserList, setFiltersObj]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      const nextPage = newPage + 1;
      getUserList({ ...filtersObj, page: nextPage });
      setFiltersObj({ ...filtersObj, page: nextPage });
    },
    [filtersObj, getUserList, setFiltersObj]
  );

  const handlePageSizeChange = useCallback(
    (nextPageSize) => {
      getUserList({
        ...filtersObj,
        limit: nextPageSize,
        page: 1,
      });
      setFiltersObj({
        ...filtersObj,
        limit: nextPageSize,
        page: 1,
      });
    },
    [filtersObj, getUserList, setFiltersObj]
  );

  const removeUserFromList = useCallback((index) => {
    setUserResponse((prevState) => {
      const updatedList = prevState?.list?.filter((_, idx) => idx !== index);
      return { ...prevState, list: updatedList };
    });
  }, []);

  const updateUserInList = useCallback((userId, patch) => {
    setUserResponse((prevState) => {
      const updatedList = (prevState?.list || []).map((item) => (
        String(item.id) === String(userId) ? { ...item, ...patch } : item
      ));
      return { ...prevState, list: updatedList };
    });
  }, []);

  const refreshList = useCallback(() => {
    getUserList(filtersObj);
  }, [filtersObj, getUserList]);

  const deleteUser = useCallback(
    async (user, index) => {
      try {
        const res = await userService.deleteUser(user?.id);
        if (res?.status === 200) {
          removeUserFromList(index);
          showSnackBar('User deleted successfully.', 'success');
          return true;
        }
        showSnackBar('Failed to delete user. Please try again.', 'error');
        return false;
      } catch {
        showSnackBar('An error occurred while deleting the user:', 'error');
        return false;
      }
    },
    [removeUserFromList, showSnackBar]
  );

  const unlockUser = useCallback(
    async (user) => {
      try {
        const res = await userService.unlockUser(user.username);
        if (res?.status === 200) {
          showSnackBar('User unlocked successfully!', 'success');
        } else {
          showSnackBar('Failed to unlock user.', 'error');
        }
      } catch {
        showSnackBar('Error unlocking user.', 'error');
      }
    },
    [showSnackBar]
  );

  const setUserStatus = useCallback(
    async (user, isActive) => {
      try {
        const res = await userService.setUserStatus(user?.id, isActive);
        if (res?.status === 200) {
          const statusFilter = filtersObj.is_active;
          const shouldRemoveFromList =
            (statusFilter === 'true' && !isActive)
            || (statusFilter === 'false' && isActive);

          if (shouldRemoveFromList) {
            setUserResponse((prevState) => ({
              ...prevState,
              count: Math.max(0, (prevState.count || 0) - 1),
              list: (prevState.list || []).filter(
                (item) => String(item.id) !== String(user.id)
              ),
            }));
          } else {
            updateUserInList(user.id, { is_active: isActive });
          }

          showSnackBar(
            isActive
              ? USER_STATUS_MESSAGES.activateSuccess
              : USER_STATUS_MESSAGES.deactivateSuccess,
            'success'
          );
          return true;
        }

        const errorPayload = res?.data?.data?.general_error;
        if (errorPayload?.error_code === 'cannot_deactivate_self') {
          showSnackBar(USER_STATUS_MESSAGES.cannotDeactivateSelf, 'info');
          return false;
        }

        showSnackBar(
          isActive
            ? USER_STATUS_MESSAGES.activateFailed
            : USER_STATUS_MESSAGES.deactivateFailed,
          'error'
        );
        return false;
      } catch {
        showSnackBar(
          isActive
            ? USER_STATUS_MESSAGES.activateFailed
            : USER_STATUS_MESSAGES.deactivateFailed,
          'error'
        );
        return false;
      }
    },
    [filtersObj.is_active, showSnackBar, updateUserInList]
  );

  const changeUserPassword = useCallback(
    async (userId, password) => {
      try {
        const res = await userService.changeUserPassword({ password, id: userId });
        if (res.status === 200) {
          showSnackBar(res?.data?.data, 'success');
          return true;
        }
        showSnackBar('Password must not match the last 8 used passwords.', 'error');
        return false;
      } catch (error) {
        showSnackBar(error?.toString(), 'error');
        return false;
      }
    },
    [showSnackBar]
  );

  return {
    users: userResponse?.list || [],
    count: userResponse?.count || 0,
    filtersObj,
    isLoading,
    handleSearch,
    handleOrganisationChange,
    handleProfileChange,
    handleStatusChange,
    applyFilters,
    handlePageChange,
    handlePageSizeChange,
    deleteUser,
    unlockUser,
    setUserStatus,
    changeUserPassword,
    refreshList,
  };
}
